/*
 * Iskra-chat — Badstugutta-merkevaren (Badstugutta/STYLING.md), IKKE Stoke.
 * Badstublå #1A385C, sand/hvit flate, Poppins (nettsidens font, § 3/§ 8),
 * nesten firkantede knapper (2 px) og mykt avrundet innhold (4 px) (§ 4).
 * Stokes oransje #ea945f skal aldri stå her.
 *
 * Isolasjon: ingen shadow DOM. Rot-elementet får `all: initial`, og alle
 * regler er prefikset med #iskra-chat-root så de slår temaets element-regler.
 */
export const css = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap');

  /* badstugutta.no har en flytende «Få din badstue-plan»-knapp (.lm-fab) med
     z-index over panelet. Skjules mens chatten er åpen (klassen settes i App.tsx). */
  html.iskra-chat-open .lm-fab { display: none !important; }

  #iskra-chat-root {
    all: initial;
    --ic-blue: #1A385C;
    --ic-blue-hover: #234976;
    --ic-blue-light: #CBDCF0;
    --ic-tint: #EDF3F8;
    --ic-ink: #12293D;
    --ic-text: #121212;
    --ic-muted: #55677A;
    --ic-sand: #FBFAF7;
    --ic-cream: #F6F4F0;
    --ic-line: #E5E1D8;
    --ic-green: #2F6B4F;
    --ic-red: #A8473A;
    --ic-ease: cubic-bezier(0.16, 1, 0.3, 1);
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: 15px;
    line-height: 1.55;
    color: var(--ic-text);
    -webkit-font-smoothing: antialiased;
    -webkit-text-size-adjust: 100%;
  }
  #iskra-chat-root *, #iskra-chat-root *::before, #iskra-chat-root *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    color: inherit;
    text-decoration: none;
    list-style: none;
    letter-spacing: normal;
    text-transform: none;
  }
  #iskra-chat-root ::selection { background: var(--ic-blue-light); color: var(--ic-ink); }
  #iskra-chat-root button { cursor: pointer; appearance: none; -webkit-tap-highlight-color: transparent; }
  #iskra-chat-root button:disabled { cursor: not-allowed; }
  #iskra-chat-root svg { display: block; flex-shrink: 0; }
  #iskra-chat-root :focus { outline: none; }
  #iskra-chat-root :focus-visible { outline: 2px solid var(--ic-blue); outline-offset: 2px; }

  /* ---------- Lukket: knappen ---------- */
  #iskra-chat-root .ic-tab {
    position: fixed;
    /* 130 px holder oss klar av .cfg__sticky (bunnlinja på produktsider). */
    bottom: 130px;
    right: 24px;
    z-index: 999998;
    display: flex;
    align-items: center;
    gap: 10px;
    height: 52px;
    padding: 0 20px 0 16px;
    background: var(--ic-blue);
    color: #fff;
    border: 2px solid var(--ic-blue);
    border-radius: 2px;
    font-size: 15px;
    font-weight: 500;
    box-shadow: 0 8px 24px -6px rgba(18, 41, 61, 0.45), 0 2px 6px rgba(18, 41, 61, 0.18);
    transition: background-color 180ms ease, box-shadow 180ms ease, transform 180ms var(--ic-ease);
    animation: ic-fade-in 300ms var(--ic-ease);
  }
  #iskra-chat-root .ic-tab:hover { background: var(--ic-blue-hover); box-shadow: 0 12px 28px -6px rgba(18, 41, 61, 0.5), 0 2px 6px rgba(18, 41, 61, 0.2); }
  #iskra-chat-root .ic-tab:active { transform: translateY(1px); }
  #iskra-chat-root .ic-tab:focus-visible { outline-color: var(--ic-blue); outline-offset: 3px; }
  #iskra-chat-root .ic-tab-icon { width: 22px; height: 22px; }

  /* ---------- Panelet ---------- */
  #iskra-chat-root .ic-panel {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 999999;
    width: 400px;
    height: 640px;
    max-height: calc(100vh - 48px);
    max-height: calc(100dvh - 48px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--ic-sand);
    border: 1px solid var(--ic-line);
    border-radius: 4px;
    box-shadow: 0 24px 48px -12px rgba(18, 41, 61, 0.32), 0 4px 12px rgba(18, 41, 61, 0.08);
    opacity: 0;
    transform: translateY(12px) scale(0.985);
    transform-origin: bottom right;
    pointer-events: none;
    visibility: hidden;
    transition: opacity 200ms ease, transform 280ms var(--ic-ease), visibility 0s linear 280ms;
  }
  #iskra-chat-root .ic-panel.ic-open {
    opacity: 1;
    transform: none;
    pointer-events: auto;
    visibility: visible;
    transition: opacity 200ms ease, transform 280ms var(--ic-ease), visibility 0s;
  }

  /* ---------- Header ---------- */
  #iskra-chat-root .ic-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 12px 14px 16px;
    background: var(--ic-blue);
    color: #fff;
    flex-shrink: 0;
  }
  #iskra-chat-root .ic-avatar {
    position: relative;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--ic-blue-light);
    color: var(--ic-blue);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  #iskra-chat-root .ic-avatar svg { width: 20px; height: 20px; }
  #iskra-chat-root .ic-avatar-dot {
    position: absolute;
    right: -1px;
    bottom: -1px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #5FB38A;
    border: 2px solid var(--ic-blue);
  }
  #iskra-chat-root .ic-header-text { flex: 1; min-width: 0; }
  #iskra-chat-root .ic-name { font-size: 16px; font-weight: 600; line-height: 1.3; }
  #iskra-chat-root .ic-status { font-size: 13px; line-height: 1.35; color: var(--ic-blue-light); }
  #iskra-chat-root .ic-close {
    width: 40px;
    height: 40px;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    flex-shrink: 0;
    transition: background-color 150ms ease;
  }
  #iskra-chat-root .ic-close svg { width: 20px; height: 20px; }
  #iskra-chat-root .ic-close:hover { background: rgba(255, 255, 255, 0.12); }
  #iskra-chat-root .ic-close:focus-visible { outline-color: var(--ic-blue-light); outline-offset: -4px; }

  /* ---------- Meldingslista ---------- */
  #iskra-chat-root .ic-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding: 20px 16px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    scrollbar-width: thin;
    scrollbar-color: var(--ic-line) transparent;
  }
  #iskra-chat-root .ic-body::-webkit-scrollbar { width: 6px; }
  #iskra-chat-root .ic-body::-webkit-scrollbar-thumb { background: var(--ic-line); border-radius: 3px; }

  #iskra-chat-root .ic-intro { padding: 4px 4px 14px; }
  #iskra-chat-root .ic-intro-title {
    font-size: 22px;
    font-weight: 600;
    line-height: 1.25;
    color: var(--ic-ink);
    margin-bottom: 8px;
    letter-spacing: -0.01em;
  }
  #iskra-chat-root .ic-intro-body { font-size: 15px; color: var(--ic-muted); max-width: 34ch; }

  #iskra-chat-root .ic-msg {
    max-width: 85%;
    padding: 10px 14px;
    font-size: 15px;
    line-height: 1.5;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    border-radius: 4px;
    animation: ic-msg-in 220ms var(--ic-ease);
  }
  #iskra-chat-root .ic-msg-iskra {
    align-self: flex-start;
    background: #fff;
    border: 1px solid var(--ic-line);
    color: var(--ic-text);
  }
  #iskra-chat-root .ic-msg-user {
    align-self: flex-end;
    background: var(--ic-blue);
    color: #fff;
  }
  #iskra-chat-root .ic-msg-operator {
    align-self: flex-start;
    background: #F1F6F3;
    border: 1px solid #CFE0D6;
  }
  #iskra-chat-root .ic-operator-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--ic-green);
    margin-bottom: 2px;
  }
  #iskra-chat-root .ic-operator-label svg { width: 14px; height: 14px; }

  /* Systemlinjer (Mattis koblet på, feilmelding) — en linje i samtalen, ikke en boble. */
  #iskra-chat-root .ic-msg-system {
    align-self: center;
    max-width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px;
    font-size: 13px;
    color: var(--ic-muted);
    text-align: center;
  }
  #iskra-chat-root .ic-msg-system::before,
  #iskra-chat-root .ic-msg-system::after {
    content: "";
    flex: 1;
    min-width: 16px;
    height: 1px;
    background: var(--ic-line);
  }

  #iskra-chat-root .ic-msg a {
    color: var(--ic-blue);
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
    overflow-wrap: anywhere;
  }
  #iskra-chat-root .ic-msg a:hover { color: var(--ic-ink); text-decoration-thickness: 2px; }
  #iskra-chat-root .ic-msg-user a { color: #fff; }

  /* ---------- Handlingsknapper i en melding ---------- */
  #iskra-chat-root .ic-actions { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; white-space: normal; }
  #iskra-chat-root .ic-msg a.ic-cta,
  #iskra-chat-root .ic-cta {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 44px;
    padding: 8px 14px;
    border: 2px solid var(--ic-blue);
    border-radius: 2px;
    background: var(--ic-blue);
    color: #fff;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.3;
    text-align: left;
    text-decoration: none;
    transition: background-color 150ms ease, border-color 150ms ease;
  }
  #iskra-chat-root .ic-msg a.ic-cta:hover,
  #iskra-chat-root .ic-cta:hover { background: var(--ic-blue-hover); border-color: var(--ic-blue-hover); color: #fff; }
  #iskra-chat-root .ic-cta svg { width: 20px; height: 20px; }
  #iskra-chat-root .ic-cta-text { display: flex; flex-direction: column; min-width: 0; }
  #iskra-chat-root .ic-cta-sub { font-size: 12px; font-weight: 400; color: var(--ic-blue-light); }
  #iskra-chat-root .ic-cta-secondary { background: #fff; color: var(--ic-blue); }
  #iskra-chat-root .ic-cta-secondary:hover { background: var(--ic-tint); border-color: var(--ic-blue); color: var(--ic-blue); }

  /* E-postutkast Iskra foreslår */
  #iskra-chat-root .ic-mail-draft {
    margin-top: 12px;
    padding: 12px;
    background: var(--ic-cream);
    border: 1px solid var(--ic-line);
    border-radius: 4px;
    white-space: normal;
  }
  #iskra-chat-root .ic-mail-draft-header { font-size: 13px; font-weight: 600; color: var(--ic-ink); margin-bottom: 8px; }
  #iskra-chat-root .ic-mail-draft-textarea {
    display: block;
    width: 100%;
    min-height: 120px;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid var(--ic-line);
    border-radius: 4px;
    font-size: 16px;
    line-height: 1.5;
    color: var(--ic-text);
    resize: vertical;
  }
  #iskra-chat-root .ic-mail-draft-textarea:focus { border-color: var(--ic-blue); outline: none; }
  #iskra-chat-root .ic-mail-draft-actions { display: flex; gap: 8px; margin-top: 10px; }
  #iskra-chat-root .ic-mail-draft-send,
  #iskra-chat-root .ic-mail-draft-hide {
    min-height: 40px;
    padding: 0 16px;
    border: 2px solid var(--ic-blue);
    border-radius: 2px;
    font-size: 14px;
    font-weight: 500;
    transition: background-color 150ms ease;
  }
  #iskra-chat-root .ic-mail-draft-send { background: var(--ic-blue); color: #fff; }
  #iskra-chat-root .ic-mail-draft-send:hover { background: var(--ic-blue-hover); border-color: var(--ic-blue-hover); }
  #iskra-chat-root .ic-mail-draft-hide { border-color: var(--ic-line); color: var(--ic-muted); background: #fff; }
  #iskra-chat-root .ic-mail-draft-hide:hover { background: var(--ic-sand); }

  /* ---------- Skriveindikator ---------- */
  #iskra-chat-root .ic-thinking-bubble { display: flex; align-items: center; gap: 12px; }
  #iskra-chat-root .ic-thinking { display: flex; gap: 4px; flex-shrink: 0; }
  #iskra-chat-root .ic-thinking span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--ic-blue);
    animation: ic-bounce 1.2s infinite ease-in-out;
  }
  #iskra-chat-root .ic-thinking span:nth-child(2) { animation-delay: 0.15s; }
  #iskra-chat-root .ic-thinking span:nth-child(3) { animation-delay: 0.3s; }
  #iskra-chat-root .ic-thinking-quote { font-size: 14px; color: var(--ic-muted); animation: ic-fade-in 300ms ease; }

  /* ---------- Skrivefeltet ---------- */
  #iskra-chat-root .ic-composer {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 12px;
    background: #fff;
    border-top: 1px solid var(--ic-line);
    flex-shrink: 0;
  }
  #iskra-chat-root .ic-composer textarea {
    flex: 1;
    min-width: 0;
    min-height: 44px;
    max-height: 120px;
    padding: 9px 12px;
    background: var(--ic-sand);
    border: 1px solid var(--ic-line);
    border-radius: 4px;
    font-size: 16px; /* ≥16px: iOS zoomer ikke inn ved fokus */
    line-height: 1.5;
    color: var(--ic-text);
    resize: none;
    overflow-y: auto;
    scrollbar-width: none;
    field-sizing: content;
    transition: border-color 150ms ease, background-color 150ms ease;
  }
  #iskra-chat-root .ic-composer textarea::placeholder { color: var(--ic-muted); opacity: 1; }
  #iskra-chat-root .ic-composer textarea:focus { outline: none; border-color: var(--ic-blue); background: #fff; }
  #iskra-chat-root .ic-composer textarea:disabled { opacity: 0.7; }
  #iskra-chat-root .ic-send {
    width: 44px;
    height: 44px;
    border-radius: 2px;
    background: var(--ic-blue);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background-color 150ms ease;
  }
  #iskra-chat-root .ic-send svg { width: 20px; height: 20px; }
  #iskra-chat-root .ic-send:hover:not(:disabled) { background: var(--ic-blue-hover); }
  #iskra-chat-root .ic-send:disabled { background: var(--ic-blue-light); color: #fff; }

  #iskra-chat-root .ic-footer {
    padding: 0 12px 12px;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
    background: #fff;
    font-size: 13px;
    color: var(--ic-muted);
    text-align: center;
    flex-shrink: 0;
  }
  #iskra-chat-root .ic-link {
    color: var(--ic-blue);
    font-weight: 500;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
  }
  #iskra-chat-root .ic-link:hover { text-decoration-thickness: 2px; }

  /* ---------- Åpningsskjema og e-postskjema ---------- */
  #iskra-chat-root .ic-gate,
  #iskra-chat-root .ic-escalation {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 28px 20px 20px;
    padding-bottom: max(20px, env(safe-area-inset-bottom));
    background: var(--ic-sand);
  }
  #iskra-chat-root .ic-gate h3,
  #iskra-chat-root .ic-escalation h3 {
    font-size: 22px;
    font-weight: 600;
    line-height: 1.25;
    color: var(--ic-ink);
    letter-spacing: -0.01em;
    margin-bottom: 8px;
  }
  #iskra-chat-root .ic-lede { font-size: 15px; color: var(--ic-muted); margin-bottom: 24px; max-width: 36ch; }
  #iskra-chat-root .ic-field { display: block; margin-bottom: 16px; }
  #iskra-chat-root .ic-field-label { display: block; font-size: 14px; font-weight: 500; color: var(--ic-ink); margin-bottom: 6px; }
  #iskra-chat-root .ic-field input,
  #iskra-chat-root .ic-field textarea {
    display: block;
    width: 100%;
    min-height: 48px;
    padding: 11px 12px;
    background: #fff;
    border: 1px solid #C9C3B6;
    border-radius: 4px;
    font-size: 16px;
    line-height: 1.5;
    color: var(--ic-text);
    transition: border-color 150ms ease, box-shadow 150ms ease;
  }
  #iskra-chat-root .ic-field input::placeholder { color: var(--ic-muted); opacity: 1; }
  #iskra-chat-root .ic-field input:focus,
  #iskra-chat-root .ic-field textarea:focus { outline: none; border-color: var(--ic-blue); box-shadow: 0 0 0 3px var(--ic-tint); }
  #iskra-chat-root .ic-field textarea { min-height: 120px; resize: vertical; }
  #iskra-chat-root .ic-err { font-size: 14px; color: var(--ic-red); margin: -4px 0 14px; }
  #iskra-chat-root .ic-submit {
    width: 100%;
    min-height: 48px;
    padding: 0 16px;
    background: var(--ic-blue);
    color: #fff;
    border: 2px solid var(--ic-blue);
    border-radius: 2px;
    font-size: 16px;
    font-weight: 500;
    transition: background-color 150ms ease;
  }
  #iskra-chat-root .ic-submit:hover:not(:disabled) { background: var(--ic-blue-hover); border-color: var(--ic-blue-hover); }
  #iskra-chat-root .ic-submit:disabled { opacity: 0.6; }
  #iskra-chat-root .ic-fineprint { font-size: 13px; color: var(--ic-muted); text-align: center; margin-top: 14px; }
  #iskra-chat-root .ic-back {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    min-height: 44px;
    margin-top: 8px;
    font-size: 14px;
    color: var(--ic-muted);
  }
  #iskra-chat-root .ic-back svg { width: 16px; height: 16px; }
  #iskra-chat-root .ic-back:hover { color: var(--ic-ink); }
  #iskra-chat-root .ic-done-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #E3EFE8;
    color: var(--ic-green);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
  }
  #iskra-chat-root .ic-done-icon svg { width: 24px; height: 24px; }

  /* ---------- Bevegelse ---------- */
  @keyframes ic-fade-in { from { opacity: 0; } to { opacity: 1; } }
  @keyframes ic-msg-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
  @keyframes ic-bounce {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
    30% { transform: translateY(-4px); opacity: 1; }
  }
  @keyframes ic-pulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }

  /* Redusert bevegelse: behold varighetene, kutt forflytningen. Prikkene
     pulserer fortsatt (en frossen indikator ser ut som at chatten har hengt seg). */
  @media (prefers-reduced-motion: reduce) {
    #iskra-chat-root .ic-panel,
    #iskra-chat-root .ic-panel.ic-open { transform: none; }
    #iskra-chat-root .ic-msg { animation-name: ic-fade-in; }
    #iskra-chat-root .ic-thinking span { animation-name: ic-pulse; }
    #iskra-chat-root .ic-tab:active { transform: none; }
  }

  /* ---------- Mobil ---------- */
  @media (max-width: 749px) {
    #iskra-chat-root .ic-tab {
      bottom: 90px;
      right: 16px;
      width: 56px;
      height: 56px;
      padding: 0;
      justify-content: center;
    }
    #iskra-chat-root .ic-tab-label { display: none; }
    #iskra-chat-root .ic-tab-icon { width: 24px; height: 24px; }

    #iskra-chat-root .ic-panel {
      inset: 0;
      width: 100%;
      height: 100%;
      height: 100dvh;
      max-height: none;
      border: 0;
      border-radius: 0;
      box-shadow: none;
      transform: translateY(24px);
      transform-origin: center bottom;
    }
    #iskra-chat-root .ic-header {
      padding-top: max(14px, env(safe-area-inset-top));
      padding-left: max(16px, env(safe-area-inset-left));
      padding-right: max(12px, env(safe-area-inset-right));
    }
    #iskra-chat-root .ic-msg { max-width: 90%; }
  }
`;
